<script setup lang="ts">
/* eslint-disable max-lines */
import { useAdminDistrictsStore } from '@/stores/adminDistrictsStore'
import { useCommonModalStore } from '@/stores/commonModalStore'
import { useInspectionResultStore } from '@/stores/inspectionResultUpsertStore'
import { useNetworkStore } from '@/stores/networkStore'
import type { Shape } from '@/types/accessor/api/common'
import {
  METER_TO_DEGREE,
  DEFAULT_LAYER_SIZE
} from '@/types/accessor/api/floodDisaster/basicGsiObject'
import DestinationLeafletMapComp from '@/components/inspection/common/DestinationLeafletMap.vue'
import type { LatLngExpression } from 'leaflet'
import { ref, onMounted, computed, type Ref } from 'vue'
import { isOnlineByHealthChecker } from '@/modules/healthCheck/healthChecker'
import { getCount } from '@/libs/leaflet/indexedDB'
import type {
  ArgQueryAdminDistricts,
  QueryResult,
  ResQueryAdminDistricts
} from '@/types/accessor/api/adminDistricts/adminDistricts'
import { accessDataBasedOnNetwork } from '@/accessors/common/dataAccessorBasedOnNetwork'
import {
  CREATE_MAP_OFFLINE_DATA_ERROR,
  EDIT_MAP_OFFLINE_DATA_ERROR,
  MAP_ERROR_TITLE
} from '@/constants/error/convertToFrontendErrorCode'
import { useUserAttributes } from '@/modules/miiaFw/composables/useUserAttributes'
import { ERROR_CODES } from '@/constants/error/errorCode'
import { GlobalError } from '@/utils/globalError/GlobalError'
import { useDisasterStore } from '@/stores/disasterStore'
import { MODAL_RESULT } from '@/modules/miiaFw/types/components/commonModal'
import { useOfflineDataDownloadProcessStore } from '@/stores/offlineDataDownloadProcessStore'
import { useLoadingView } from '@/modules/miiaFw/composables/useLoadingView'

const commonModalStore = useCommonModalStore()
const adminDistrictsStore = useAdminDistrictsStore()
const networkStore = useNetworkStore()
const inspectionResultStore = useInspectionResultStore()
const disasterStore = useDisasterStore()
const { getUserAttributesFromStore } = useUserAttributes()
const offlineDataDownloadProcessStore = useOfflineDataDownloadProcessStore()
const loadingView = useLoadingView()

const props = defineProps({
  errorCallBack: {
    required: true
  },
  isMapError: {
    type: Boolean,
    required: true
  },
  isViewMap: {
    type: Boolean,
    required: true
  }
})

const emit = defineEmits<{
  (event: 'updateSelectedBuilding', isSelected: boolean): void
  (event: 'update:isViewMap', value: boolean): void
}>()

const leafletRef = ref<InstanceType<typeof DestinationLeafletMapComp> | null>(null)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const anyProps: any = props
const preLocation = ref<number[]>([])
const preShape = ref<Shape>({
  type: 'MultiPolygon',
  coordinates: []
})
const isAvailableOfflineMap = ref<boolean>(false)
const mapErrorMes = ref<string[]>([])
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const adminStreetDistrictsItem = ref<any>({})
const isEdit = ref<boolean>(false)

const target: Ref<{
  location: number[]
  shape?: {
    type: string
    coordinates: number[][][][]
  }
}> = ref({
  location: [],
  shape: { type: '', coordinates: [] }
})
const mapErrorMessages = computed(() => {
  if (mapErrorMes.value === undefined || mapErrorMes.value.length === 0) {
    return ''
  }
  const messages = mapErrorMes.value.join('\n')
  return messages
})

/**
 * データのない地点がクリックされた際の確認モーダル及び選択された建物のピン更新
 * @param latLng 調査対象家屋の経緯度
 * @returns なし
 */
const openModal = (latLng: number[]) => {
  preLocation.value = latLng
  // ここでピンを打つ。そのためにここでshapeを作る
  const width = METER_TO_DEGREE * DEFAULT_LAYER_SIZE
  const northWest = [preLocation.value[1] - width, preLocation.value[0] + width]
  const northEast = [preLocation.value[1] + width, preLocation.value[0] + width]
  const southEast = [preLocation.value[1] + width, preLocation.value[0] - width]
  const southWest = [preLocation.value[1] - width, preLocation.value[0] - width]
  preShape.value = {
    type: 'MultiPolygon',
    coordinates: [[[northWest, northEast, southEast, southWest, northWest]]]
  }
  leafletRef.value!.drawTargetBuilding(
    leafletRef.value!.generateLatLngListBaseShape(preShape.value.coordinates),
    target.value.location
  )
  commonModalStore
    .openConfirmModal({
      title: '確認',
      body: '建物がない地点が選択されました。新規に建物を追加しますか？',
      okTitle: '追加',
      cancelTitle: 'キャンセル'
    })
    .then(async result => {
      if (result === MODAL_RESULT.OK) {
        clickedOnMap()
      }
    })
    .catch(async result => {
      if (result === MODAL_RESULT.CANCEL) {
        resetTargetBuilding()
      }
    })
}

/**
 * モーダルで追加ボタンを押下したときの処理
 * @param なし
 * @returns なし
 */
const clickedOnMap = () => {
  leafletRef.value!.clickedOnMap(preLocation.value, preShape.value)
}

/**
 * モーダルでキャンセルボタンを押下したときの処理
 * @param なし
 * @returns なし
 */
const resetTargetBuilding = () => {
  leafletRef.value!.resetTargetBuilding()
}

/**
 * 画面にマップを表示
 * @params なし
 * @return なし
 **/
const mappingLeaflet = async () => {
  // オフラインの場合
  const count = await getCount('downloadedFeature')
  if (count && count > 0) {
    offlineDataDownloadProcessStore.updateIsDownloaded(true)
  }

  const isViewMap = isAvailableOfflineMap.value || offlineDataDownloadProcessStore.isDownloaded
  emit('update:isViewMap', isViewMap)

  if (!isViewMap) {
    if (isEdit.value) {
      mapErrorControl(EDIT_MAP_OFFLINE_DATA_ERROR, MAP_ERROR_TITLE, 500)
    } else {
      mapErrorControl(CREATE_MAP_OFFLINE_DATA_ERROR, MAP_ERROR_TITLE, 500)
    }
    handleOfflineTargetBuilding()
    return
  }

  if (!leafletRef.value) {
    return
  }
  await leafletRef.value.createMap(
    [...adminStreetDistrictsItem.value[0].location].reverse() as LatLngExpression,
    13,
    disasterStore.disaster?.govCode
  )
  leafletRef.value.onTouchToGetLocation()
  leafletRef.value.controlMoveEndEvent()
  if (target.value.location.length > 0 && target.value.shape) {
    leafletRef.value.drawTargetBuilding(
      leafletRef.value.generateLatLngListBaseShape(target.value.shape.coordinates),
      target.value.location
    )
    // オフライン時に自治体範囲外を描画しようとした場合、選択済みの建物位置にリセット
    leafletRef.value.flyToLocation(
      [...target.value.location].reverse() as unknown as LatLngExpression,
      18
    )
  }
}

/**
 * エラー時の処理
 * @param message エラーメッセージ
 * @param title エラータイトル
 * @param code エラーコード
 */
const mapErrorControl = (message: string, title?: string, code?: number | string) => {
  if (code !== undefined) {
    mapErrorMes.value = [...mapErrorMes.value, message]
  }
  if (anyProps.errorCallBack !== undefined) {
    anyProps.errorCallBack(message, title, code)
  }
}

/**
 * オフライン時に建物情報データを生成する
 * @param なし
 * @returns なし
 */
const handleOfflineTargetBuilding = async () => {
  // 建物ありの場合は建物情報があるのでそれをそのまま使用する
  if (inspectionResultStore.building.buildingId) {
    return
  }
  const attribute = await getUserAttributesFromStore()
  const adminDistrictsQueryParams: ArgQueryAdminDistricts = {
    govCode: attribute.attribute['disaster-commonattr'].govCode ?? '',
    type: 'street'
  }
  const res = (await accessDataBasedOnNetwork(
    'queryAdminDistrictsDaoApi',
    adminDistrictsQueryParams,
    false,
    () => {
      networkStore.setOfflineStatus()
    }
  ).catch(err => {
    console.error(err)
    throw new GlobalError([ERROR_CODES.BUILDING_OVERVIEW_ADMIN_DISTRICTS_GET_FAILED])
  })) as ResQueryAdminDistricts
  const adminDistrictsStreetItems = res.result
  if (adminDistrictsStreetItems.length === 0) return
  target.value = generateOfflineComputedTarget(adminDistrictsStreetItems[0])
}

/**
 * computedTargetに引数の建物情報データを付加した調査対象を生成する
 * @param adminDistrictsStreetItem 行政区画情報のstreet
 * @returns 調査対象のオブジェクト
 */
const generateOfflineComputedTarget = (
  adminDistrictsStreetItem: QueryResult
): { location: number[]; shape?: Shape } => {
  const latLng = {
    lat: adminDistrictsStreetItem.location[0],
    lng: adminDistrictsStreetItem.location[1]
  }
  const width = METER_TO_DEGREE * DEFAULT_LAYER_SIZE
  const northWest = [
    adminDistrictsStreetItem.location[0] - width,
    adminDistrictsStreetItem.location[1] + width
  ]
  const northEast = [
    adminDistrictsStreetItem.location[0] + width,
    adminDistrictsStreetItem.location[1] + width
  ]
  const southEast = [
    adminDistrictsStreetItem.location[0] + width,
    adminDistrictsStreetItem.location[1] - width
  ]
  const southWest = [
    adminDistrictsStreetItem.location[0] - width,
    adminDistrictsStreetItem.location[1] - width
  ]
  preShape.value = {
    type: 'MultiPolygon',
    coordinates: [[[northWest, northEast, southEast, southWest, northWest]]]
  }
  return {
    ...target.value,
    location: Object.values(latLng),
    shape: preShape.value
  }
}

/**
 * 建物が選択されたことをemit
 * @param isSelected true: 選択済, false: 未選択
 * @returns なし
 */
const updateSelectedBuilding = (isSelected: boolean): void => {
  emit('updateSelectedBuilding', isSelected)
}

// ライフサイクル
onMounted(async () => {
  loadingView.setLoadingView(true)

  // 調査編集か新規調査か判別
  isEdit.value = inspectionResultStore.building.location != null

  adminStreetDistrictsItem.value = adminDistrictsStore.getStreetResult
  isAvailableOfflineMap.value = await (async () => {
    // レンダリング前にオンライン状態をチェック
    const isConectOnline = await isOnlineByHealthChecker()
    const isOnline = networkStore.isOnline
    // ネットワーク接続可能かつオンラインモード
    if (isConectOnline && isOnline) return true
    // オフライン地図が中断またはエラーの場合は利用制限させる
    const availableOfflineMapState = window.localStorage.getItem('availableOfflineMap')
    if (availableOfflineMapState == null) return false
    // 中断またはエラー終了判定
    if (availableOfflineMapState === '1') {
      // indexedDBデータがなければ利用不可
      const cnt = await getCount('downloadedFeature')
      if (cnt === undefined) return false
      if (cnt === 0) return false
      return true
    }
    return false
  })()

  // 地図系の処理
  await mappingLeaflet()
  if (
    leafletRef.value &&
    inspectionResultStore.building.location &&
    inspectionResultStore.building.shapeCoordinates &&
    (networkStore.isOnline || offlineDataDownloadProcessStore.isDownloaded)
  ) {
    leafletRef.value.setStoreToMap()
  }

  loadingView.setLoadingView(false)
})
</script>

<template>
  <div>
    <DestinationLeafletMapComp
      v-show="!isMapError"
      ref="leafletRef"
      :idName="'buildingInfoMap'"
      :disabledShowAll="true"
      @dataPush="openModal"
      @updateSelectedBuilding="updateSelectedBuilding"
    />
    <div v-if="isMapError" class="map-error-contents">
      <div class="map-error-msg">{{ mapErrorMessages }}</div>
    </div>
  </div>
</template>

<style>
@media screen and (min-width: 821px) {
  .map-container {
    height: 510px;
  }
}

/*ipadサイズ用*/
@media screen and (max-width: 820px) {
  .map-container {
    height: 400px;
  }
}

.map-error-msg {
  white-space: pre-line;
  margin-top: 15px;
  margin-bottom: 10px;
}

.map-error-contents {
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>
